import { getCurrentScope, onScopeDispose, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';

import { useAccessControlStore } from '../store/access-control.store';
import type { AccessDenialReason, AccessRequirementKey } from '../store/access-control.store';

const ACCESS_DENIED_TOAST_COOLDOWN_MS = 5_000;
let lastAccessDeniedToastAt = 0;
const shouldShowAccessDeniedToast = ref(false);
let accessDeniedToast: ReturnType<typeof useToast> | null = null;

export interface EnsureHasAccessOptions {
  showToast?: boolean;
}

const getAccessDeniedToastDetail = (reason: AccessDenialReason | null) => {
  if (!reason) {
    return 'This feature will be available once account verification is complete.';
  }

  if (reason.accessState === 'resolving') {
    return 'Account requirements are still being checked. This feature will be available once verification is complete.';
  }

  if (reason.blockingRequirementKeys.length > 0) {
    return 'Access is unavailable until the required onboarding steps are completed.';
  }

  if (reason.pendingRequirementKeys.length > 0) {
    return 'Access is pending while your information is being processed.';
  }

  return 'This feature will be available once account verification is complete.';
};

const showAccessDeniedToast = () => {
  const accessControlStore = useAccessControlStore();

  if (!accessDeniedToast) {
    return false;
  }

  const now = Date.now();
  if (now - lastAccessDeniedToastAt < ACCESS_DENIED_TOAST_COOLDOWN_MS) {
    return false;
  }

  try {
    accessDeniedToast.add({
      severity: 'warn',
      summary: 'Account verification pending',
      detail: getAccessDeniedToastDetail(accessControlStore.getAccessDenialReason()),
    });
    lastAccessDeniedToastAt = now;
    return true;
  } catch {
    // Toast plugin is not available in this execution context.
    return false;
  }
};

const requestAccessDeniedToast = () => {
  shouldShowAccessDeniedToast.value = true;
};

const clearRequestedAccessDeniedToast = () => {
  shouldShowAccessDeniedToast.value = false;
};

export const useEnsureHasAccessToast = () => {
  const accessControlStore = useAccessControlStore();
  const toast = useToast();

  accessDeniedToast = toast;

  if (getCurrentScope()) {
    onScopeDispose(() => {
      if (accessDeniedToast === toast) {
        accessDeniedToast = null;
      }
    });
  }

  watch(
    [
      shouldShowAccessDeniedToast,
      () => accessControlStore.isAccessResolved,
      () => accessControlStore.isOnboarded,
      () => accessControlStore.accessState,
    ],
    ([shouldShowToast, isAccessResolved, isOnboarded, accessState]) => {
      if (!shouldShowToast || !isAccessResolved) {
        return;
      }

      if (accessState === 'unauthenticated') {
        clearRequestedAccessDeniedToast();
        return;
      }

      if (isOnboarded) {
        clearRequestedAccessDeniedToast();
        return;
      }

      if (showAccessDeniedToast()) {
        clearRequestedAccessDeniedToast();
      }
    },
    { immediate: true },
  );
};

/**
 * Returns whether the user can access protected data/features.
 * Optionally shows a deduped toast when access is denied.
 */
export const ensureHasAccess = (options: EnsureHasAccessOptions = {}): boolean => {
  const { showToast = true } = options;
  const accessControlStore = useAccessControlStore();

  if (accessControlStore.accessState === 'unauthenticated') {
    clearRequestedAccessDeniedToast();
    return false;
  }

  if (accessControlStore.isOnboarded) {
    clearRequestedAccessDeniedToast();
    return true;
  }

  if (!showToast) {
    return false;
  }

  requestAccessDeniedToast();

  if (!accessControlStore.isAccessResolved) {
    return false;
  }

  if (showAccessDeniedToast()) {
    clearRequestedAccessDeniedToast();
  }

  return false;
};

export const getAccessDenialReason = (): AccessDenialReason | null => {
  return useAccessControlStore().getAccessDenialReason();
};

export const ensureRequirementSatisfied = (
  requirementKey: AccessRequirementKey,
  options: EnsureHasAccessOptions = {},
): boolean => {
  const { showToast = true } = options;
  const accessControlStore = useAccessControlStore();

  if (accessControlStore.accessState === 'unauthenticated') {
    clearRequestedAccessDeniedToast();
    return false;
  }

  if (accessControlStore.hasRequirementSatisfied(requirementKey)) {
    clearRequestedAccessDeniedToast();
    return true;
  }

  if (!showToast) {
    return false;
  }

  requestAccessDeniedToast();

  if (!accessControlStore.isAccessResolved) {
    return false;
  }

  if (showAccessDeniedToast()) {
    clearRequestedAccessDeniedToast();
  }

  return false;
};
